
import React, { useState } from 'react';
import { Region } from '../../data/types/region-types';
import { City } from '../../data/types/city-types';
import { Button } from '@/components/ui/button';
import { ChevronLeft, Maximize2, Minimize2, Users, Church, Globe, Heart } from 'lucide-react';
import { useLanguage } from '../../contexts/language';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import YouTubeEmbed from '../video/YouTubeEmbed';
import UnreachedGroups from './UnreachedGroups';
import RegionVideo from './RegionVideo';
import CitiesList from './CitiesList';
import RecentDonations from './RecentDonations';
import DonationForm from './DonationForm';
import Churches from './churches';
import FundingProgress from '../regions/FundingProgress';
import RegionStats from '../regions/RegionStats';
import { getUnreachedGroupsByRegion } from '../../data/utils/region-utils';

interface RegionSidebarProps {
  region: Region;
  onClose: () => void;
  onCitySelect: (cityId: string) => void;
  cities?: City[];
  style?: string; // Add the missing style prop
}

const RegionSidebar: React.FC<RegionSidebarProps> = ({ 
  region, 
  onClose, 
  onCitySelect, 
  cities = [] 
}) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('overview');
  const [isMaximized, setIsMaximized] = useState(false);
  const [showUnreachedData, setShowUnreachedData] = useState(false);
  const [showDonationForm, setShowDonationForm] = useState(false);

  const getCityColor = (presence: string): string => {
    switch(presence) {
      case 'High': return 'bg-green-500';
      case 'Medium': return 'bg-yellow-500';
      case 'Low': return 'bg-orange-500';
      case 'Unreached': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getCityFundingPercentage = (city: City): number => {
    if (!city.funding_amount || !city.pledges) return 0;
    return Math.min(Math.round((city.pledges / city.funding_amount) * 100), 100);
  };

  const handlePledgeClick = () => {
    setActiveTab('pledge');
  };

  const MainContent = () => (
    <div className="flex-1 overflow-auto">
      <Tabs defaultValue="overview" value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <div className="px-4 pt-2">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
            <TabsTrigger value="churches">{t('churches')}</TabsTrigger>
            <TabsTrigger value="cities">{t('cities')}</TabsTrigger>
            <TabsTrigger value="pledge">{t('pledge')}</TabsTrigger>
          </TabsList>
        </div>
        
        <div className="flex-1 overflow-auto">
          {/* Overview Tab */}
          <TabsContent value="overview" className="p-4 flex-1">
            {/* Region Video */}
            <div className="space-y-4 mb-6">
              <Card>
                <CardContent className="p-4">
                  <RegionVideo region={region} />
                </CardContent>
              </Card>
            </div>

            {/* Funding Progress */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <FundingProgress 
                  region={region} 
                  onPledgeClick={handlePledgeClick} 
                />
              </CardContent>
            </Card>
            
            {/* Region Stats */}
            <Card className="mb-4">
              <CardContent className="p-4">
                <RegionStats region={region} />
              </CardContent>
            </Card>

            {/* Unreached Groups */}
            <UnreachedGroups 
              region={region}
              unreachedGroups={getUnreachedGroupsByRegion(region.region_id)}
              showData={showUnreachedData}
              onToggle={() => setShowUnreachedData(!showUnreachedData)}
              unreachedPopulation={region.unreached_population || 0}
            />
          </TabsContent>
          
          {/* Churches Tab */}
          <TabsContent value="churches" className="p-4">
            <Churches region={region} />
          </TabsContent>

          {/* Cities Tab */}
          <TabsContent value="cities" className="p-4">
            <CitiesList 
              cities={cities}
              onCitySelect={onCitySelect}
              getCityColor={getCityColor}
              getCityFundingPercentage={getCityFundingPercentage}
            />
          </TabsContent>

          {/* Pledge Tab */}
          <TabsContent value="pledge" className="p-4">
            <DonationForm 
              regionId={region.region_id}
              regionName={region.english_name}
              onCancel={() => setActiveTab('overview')}
            />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
  
  return (
    <>
      {/* Regular Sidebar */}
      <div className={`fixed bottom-0 left-0 right-0 z-10 bg-white rounded-t-xl shadow-xl lg:w-[400px] lg:left-auto lg:top-0 lg:bottom-0 lg:rounded-none lg:rounded-l-xl overflow-hidden flex flex-col lg:h-full ${isMaximized ? 'hidden' : ''}`}>
        {/* Header */}
        <div className="px-4 py-3 bg-gradient-to-r from-purple-700 to-purple-500 text-white flex items-center justify-between">
          <Button 
            variant="ghost" 
            size="icon" 
            className="text-white hover:text-white hover:bg-white/20" 
            onClick={onClose}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h2 className="text-xl font-bold">{region.english_name}</h2>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:text-white hover:bg-white/20"
            onClick={() => setIsMaximized(true)}
          >
            <Maximize2 className="h-5 w-5" />
          </Button>
        </div>
        
        <MainContent />
      </div>

      {/* Maximized Dialog */}
      <Dialog open={isMaximized} onOpenChange={setIsMaximized}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
          <div className="px-4 py-3 bg-gradient-to-r from-purple-700 to-purple-500 text-white flex items-center justify-between rounded-t-lg">
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-white hover:text-white hover:bg-white/20" 
              onClick={onClose}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <h2 className="text-xl font-bold">{region.english_name}</h2>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:text-white hover:bg-white/20"
              onClick={() => setIsMaximized(false)}
            >
              <Minimize2 className="h-5 w-5" />
            </Button>
          </div>
          
          <MainContent />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RegionSidebar;
